@if (!isVendorSession) {
<div class="ria-global-shell" [class.ria-open]="isOpen">
  <button
    type="button"
    class="ria-launcher"
    aria-label="Open RIA risk intelligence agent"
    [attr.aria-expanded]="isOpen"
    (click)="toggleChat()"
  >
    <span class="ria-launcher__pulse ria-launcher__pulse--one"></span>
    <span class="ria-launcher__pulse ria-launcher__pulse--two"></span>
    <span class="ria-launcher__orb">
      <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
        <path d="M16 3.7l1.8 6.2c.2.7.8 1.3 1.5 1.5l6.2 1.8-6.2 1.8c-.7.2-1.3.8-1.5 1.5L16 22.7l-1.8-6.2c-.2-.7-.8-1.3-1.5-1.5l-6.2-1.8 6.2-1.8c.7-.2 1.3-.8 1.5-1.5L16 3.7z" />
        <path d="M24 20.5l.8 2.7c.1.3.3.5.6.6l2.7.8-2.7.8c-.3.1-.5.3-.6.6L24 28.7l-.8-2.7c-.1-.3-.3-.5-.6-.6l-2.7-.8 2.7-.8c.3-.1.5-.3.6-.6l.8-2.7z" />
      </svg>
    </span>
    <span class="ria-launcher__status" aria-hidden="true"></span>
  </button>

  @if (isOpen) {
    <div class="quick-chat-overlay" (click)="closeChat()"></div>

    <section
      class="ria-chat-window"
      role="dialog"
      aria-modal="false"
      aria-label="RIA risk intelligence agent"
    >
      <header class="ria-chat-header">
        <div class="ria-chat-title">
          <span class="ria-bot-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M12 2a1 1 0 0 1 1 1v1.07A7.99 7.99 0 0 1 20 12v4a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3v-4a7.99 7.99 0 0 1 7-7.93V3a1 1 0 0 1 1-1Zm-5.5 10v4A.5.5 0 0 0 7 16.5h10a.5.5 0 0 0 .5-.5v-4a5.5 5.5 0 1 0-11 0Zm2.25 1.25a1.25 1.25 0 1 1 2.5 0 1.25 1.25 0 0 1-2.5 0Zm4 0a1.25 1.25 0 1 1 2.5 0 1.25 1.25 0 0 1-2.5 0ZM8 20.5h8a1 1 0 1 1 0 2H8a1 1 0 1 1 0-2Z" />
            </svg>
          </span>
          <div>
            <p>RIA</p>
            <h2>Your Risk Intelligence Agent</h2>
          </div>
        </div>
        <div class="ria-chat-actions">
          <button type="button" class="ria-icon-button" aria-label="Refresh RIA context" (click)="resetChat()">
            <span aria-hidden="true">↻</span>
          </button>
          <button type="button" class="ria-icon-button" aria-label="Close RIA" (click)="closeChat()">
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </header>

      <div class="ria-context-strip">
        <span class="ria-live-dot" aria-hidden="true"></span>
        @if (isLoadingContext) {
          <span>Syncing portfolio context...</span>
        } @else {
          <span>Portfolio-wide TPRM assistant</span>
        }
      </div>

      <div class="ria-chat-body" #riaMessageBody>
        <div class="ria-message-list" aria-live="polite">
          @for (message of messages; track $index) {
            <article class="ria-message" [class.ria-message--user]="message.role === 'user'">
              <div class="ria-message-bubble">
                <p>{{ message.text }}</p>
                <time>{{ message.time }}</time>
              </div>

              @if (message.records?.length) {
                <div class="ria-record-grid" aria-label="RIA matching third party records">
                  @for (record of message.records; track record.name) {
                    <article class="ria-record-card">
                      <div>
                        <p class="ria-record-kicker">{{ record.subtitle }}</p>
                        <h3>{{ record.name }}</h3>
                      </div>
                      <dl>
                        <div>
                          <dt>Risk</dt>
                          <dd>{{ record.risk }}</dd>
                        </div>
                        <div>
                          <dt>Status</dt>
                          <dd>{{ record.status }}</dd>
                        </div>
                        <div>
                          <dt>Score</dt>
                          <dd>{{ record.score }}</dd>
                        </div>
                      </dl>
                      <div class="ria-record-actions">
                        <button type="button" (click)="explainRecord(record)">Explain</button>
                        <button type="button" (click)="openRecord(record)">Open source</button>
                      </div>
                    </article>
                  }
                </div>
              }
            </article>
          }

          @if (isThinking) {
            <article class="ria-message">
              <div class="ria-message-bubble ria-thinking">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </article>
          }
        </div>
      </div>

      @if (!hasChatStarted) {
        <div class="ria-suggestion-area">
          <p>Try asking:</p>
          <div class="ria-suggestion-chips">
            @for (prompt of suggestedPrompts; track prompt) {
              <button type="button" (click)="ask(prompt)">{{ prompt }}</button>
            }
          </div>
        </div>
      }

      <form class="ria-input-bar" (submit)="submitMessage($event)">
        <label class="sr-only" for="riaGlobalPrompt">Ask RIA about your portfolio</label>
        <input
          id="riaGlobalPrompt"
          type="text"
          autocomplete="off"
          [value]="draft"
          (input)="draft = $any($event.target).value"
          placeholder="Ask about your portfolio..."
        />
        <button type="submit" [disabled]="!draft.trim() || isThinking" aria-label="Send question to RIA">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M3.4 20.4 21.2 12 3.4 3.6 3 10.2l10.4 1.8L3 13.8l.4 6.6Z" />
          </svg>
        </button>
      </form>

      <p class="ria-disclaimer">RIA can make mistakes. Verify critical information before actioning portfolio decisions.</p>
    </section>
  }
</div>
}
